import { emptyList, ItemList } from '../../../common/models/ItemList'
import { Observable, of } from 'rxjs'
import { Container, IInit } from '../../../common/container/Container'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { Query } from '../../../common/api/Query'
import { toModel, Translation, TranslationDTO, TranslationQuery } from '../models/Translation'
import { TranslationContainerConfig } from '../container'
import { prepareURL } from '../../../common/api/http-helpers'
import { catchError, map } from 'rxjs/operators'

export interface ITranslationApi extends IInit {
  getByID(id: string): Observable<Translation | undefined>
  getFilteredList(q: Query<TranslationQuery>): Observable<ItemList<Translation>>
  add(e: TranslationDTO): Observable<Translation | undefined>
  update(e: TranslationDTO): Observable<Translation | undefined>
  delete(id: string): Observable<boolean>
}

export class TranslationApi implements ITranslationApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as TranslationContainerConfig).moduleFullUrl
  }

  add(e: TranslationDTO): Observable<Translation | undefined> {
    return this._httpClient.post<Translation>({ url: this._url, body: e }).pipe(
      map<TranslationDTO, Translation>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: this._url + '/' + id }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Translation | undefined> {
    return this._httpClient.get<Translation>({ url: `${this._url}/${id}` }).pipe(
      map<TranslationDTO, Translation>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  getFilteredList(q: Query<TranslationQuery>): Observable<ItemList<Translation>> {
    return this._httpClient.get<ItemList<Translation>>({ url: prepareURL(this._url, q) }).pipe(
      map<ItemList<TranslationDTO>, ItemList<Translation>>((dto) => {
        const itemList = emptyList<Translation>()
        itemList.count = dto.count
        itemList.items = dto.items.map((d) => toModel(d))
        return itemList
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(emptyList<Translation>())
      })
    )
  }

  update(e: TranslationDTO): Observable<Translation | undefined> {
    return this._httpClient.put<Translation>({ url: this._url, body: e }).pipe(
      map<TranslationDTO, Translation>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }
}
